import type {
    DataGridCellFocusMode,
    TableColumnDefinition,
    TableColumnId} from "@fluentui/react-components";
import {
    Avatar,
    Button,
    createTableColumn,
    Field,
    Radio,
    RadioGroup,
    TableCellLayout,
    Text
} from "@fluentui/react-components";
import type {
    RowRenderer
} from '@fluentui-contrib/react-data-grid-react-window';
import {
    DataGridBody,
    DataGrid,
    DataGridRow,
    DataGridHeader,
    DataGridCell,
    DataGridHeaderCell
} from '@fluentui-contrib/react-data-grid-react-window';
import { DismissRegular } from "@fluentui/react-icons";
import type { IConfigurationInfo, IUserDetail, IGroupDetail, IdentityDetail, IGraphPickerItem } from "../../interfaces";
import { EUserSelection } from "../../interfaces";
import { UserPicker } from "./UserPicker/UserPicker";
import { useEffect, useState } from "react";
import { UserTagGroup } from "./UserTagGroup/UserTagGroup";


export interface IAssignUsersProps {
    configurationInfo?: IConfigurationInfo;
    isLoading?: boolean;
    userSelection: EUserSelection;
    selectedPeople: IGraphPickerItem[];
    userDetails: IUserDetail[];
    groupDetails: IGroupDetail[];
    usersToBeAdded: IUserDetail[];
    groupsToBeAdded: IGroupDetail[];
    usersToBeRemoved?: IUserDetail[];
    groupsToBeRemoved?: IGroupDetail[];
    onPeopleSelect(people: IGraphPickerItem[]): void;
    onUsersRemove?(userIds: string[]): void;
    onGroupsRemove?(groupIds: string[]): void;
    onUserToBeRemovedRemove?(userId: string): void;
    onGroupToBeRemovedRemove?(groupId: string): void
    onUserSelectionChange(value: EUserSelection): void;
}

const getCellFocusMode = (columnId: TableColumnId): DataGridCellFocusMode => {
    switch (columnId) {
        case "singleAction":
            return "none";
        case "actions":
            return "group";
        default:
            return "cell";
    }
};

const renderRow: RowRenderer<IdentityDetail> = ({ item, rowId }, style) => (
    <DataGridRow<IdentityDetail> key={rowId} style={style} >
        {({ renderCell, columnId }) => (
            <DataGridCell focusMode={getCellFocusMode(columnId)}>
                {renderCell(item)}
            </DataGridCell>
        )}
    </DataGridRow>
);

const GetRowId = (item: IdentityDetail) => { return `${item.id}`; };

const getDataGridHeight = (): number => {
    const windowDataGridMargin = 650;
    const minWindowSize = 750;
    const currentWindowHeight = Math.max(window.innerHeight, minWindowSize);
    return currentWindowHeight - windowDataGridMargin;
};

export const AssignUsers = (props: IAssignUsersProps) => {
    const { configurationInfo,
        userDetails,
        groupDetails,
        selectedPeople,
        usersToBeAdded,
        usersToBeRemoved,
        groupsToBeAdded,
        groupsToBeRemoved,
        userSelection,
        isLoading,
        onUsersRemove,
        onGroupsRemove,
        onPeopleSelect,
        onUserToBeRemovedRemove,
        onGroupToBeRemovedRemove,
        onUserSelectionChange
    } = props;

    const [dataGridHeight, setDataGridHeight] = useState(getDataGridHeight());

    const items: IdentityDetail[] = [...userDetails, ...groupDetails];

    useEffect(() => {
        function handleResize() {
            setDataGridHeight(getDataGridHeight());
        }

        window.addEventListener('resize', handleResize);
        return function cleanupListener() {
            window.removeEventListener('resize', handleResize);
        };
    });

    const onRemoveAssignedUser = (id: string): void => {
        const updatedUserIds = configurationInfo?.userIds?.filter(u => u !== id) ?? [];
        onUsersRemove?.(updatedUserIds);
    };

    const onRemoveAssignedGroup = (id: string,): void => {
        const updatedGroupIds = configurationInfo?.groupIds?.filter(g => g !== id) ?? [];
        onGroupsRemove?.(updatedGroupIds);
    };

    const columns: TableColumnDefinition<IdentityDetail>[] = [
        createTableColumn<IdentityDetail>({
            columnId: "name",
            renderHeaderCell: () => { return ""; },
            renderCell: (detail) => {
                return (
                    <TableCellLayout
                        truncate
                        media={
                            <Avatar
                                aria-label={detail.displayName}
                                name={detail.displayName}
                            />
                        }>
                        {detail.displayName}
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<IdentityDetail>({
            columnId: "description",
            renderHeaderCell: () => { return ""; },
            renderCell: (detail) => {
                return (
                    <TableCellLayout truncate>
                        {detail.type === "user" ? detail.email : detail.description}
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<IdentityDetail>({
            columnId: "actions",
            renderHeaderCell: () => { return ""; },
            renderCell: (detail) => {
                const onClick = detail.type === "group" ? onRemoveAssignedGroup : onRemoveAssignedUser;
                return (<Button appearance="transparent" aria-label="Delete" onClick={() => { onClick(detail.id); } } icon={<DismissRegular />} />);
            },
        }),
    ];
 
    const columnSizingOptions = {
        name: {
            minWidth: 50,
            defaultWidth: 100,
            idealWidth: 380,
        },
        description: {
            minWidth: 100,
            idealWidth: 400,
        },
        actions: {
            minWidth: 20,
            defaultWidth: 20,
            idealWidth: 20,
        },
    };

    return (
        <>
            <Field size="large">
                <RadioGroup value={userSelection} onChange={(_, data) => { onUserSelectionChange(data.value as EUserSelection); }} disabled={isLoading}>
                    <Radio value={EUserSelection.Organization} label="Entire organization" />
                    <Radio value={EUserSelection.User} label="Specific users" />
                    <Radio value={EUserSelection.Group} label="Specific users/groups" />
                </RadioGroup>
                <UserPicker isEnabled={(userSelection === EUserSelection.User || userSelection === EUserSelection.Group) && !isLoading} includeGroups={userSelection === EUserSelection.Group} onPeopleSelect={onPeopleSelect} selectedPeople={selectedPeople} />
            </Field>
            <UserTagGroup
                fieldLabel="Users to be added"
                items={usersToBeAdded} />
            <UserTagGroup
                fieldLabel="Groups to be added"
                items={groupsToBeAdded} />
            <UserTagGroup
                fieldLabel="Users to be removed"
                items={usersToBeRemoved}
                isDismissible={true}
                onRemoveItem={(id) => { onUserToBeRemovedRemove?.(id); }}
                isLoading={isLoading} />
            <UserTagGroup
                fieldLabel="Groups to be removed"
                items={groupsToBeRemoved}
                isDismissible={true}
                onRemoveItem={(id) => { onGroupToBeRemovedRemove?.(id); }}
                isLoading={isLoading} />
            <div>
                {userSelection === EUserSelection.Organization &&
                    <Field label="Added users" size="large">
                        <Text>Entire organization</Text>
                    </Field>}
                {(userSelection === EUserSelection.User || userSelection === EUserSelection.Group) && (userDetails.length > 0 || groupDetails.length > 0) &&
                    <Field label="Added users" size="large">
                        <DataGrid
                            items={items}
                            columns={columns}
                            getRowId={GetRowId}
                            selectionAppearance="none"
                            subtleSelection
                            resizableColumns
                            columnSizingOptions={columnSizingOptions}>
                            <DataGridHeader>
                                <DataGridRow>
                                    {({ renderHeaderCell }) => (
                                        <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                                    )}
                                </DataGridRow>
                            </DataGridHeader>
                            <DataGridBody<IdentityDetail> itemSize={50} height={dataGridHeight}>
                                {renderRow}
                            </DataGridBody>
                        </DataGrid>
                    </Field>
                }
            </div>
        </>
    );
};
